/* eslint-disable @next/next/no-img-element */
import type { CSSProperties, FC, ReactNode } from 'react'

import { useEffect, useRef, useState } from 'react'

import {
    gsap,
    ms,
    registerGsap,
    useGSAP,
    wfEase
} from '@/lib/gsap'

type Slide = {
    src: string
    srcSet?: string
    sizes?: string
}

const slides: Slide[] = [
    {
        src: '/images/ChatList-White.jpg',
        srcSet: '/images/ChatList-White-p-500.jpeg 500w, /images/ChatList-White.jpg 750w',
        sizes: '(max-width: 479px) 173px, (max-width: 767px) 73vw, 25vw'
    },
    { src: '/images/SD-App.svg' },
    {
        src: '/images/App2.webp',
        srcSet: '/images/App2-p-500.jpg 500w, /images/App2-p-800.jpg 800w, /images/App2.webp 1020w',
        sizes: '(max-width: 479px) 179px, (max-width: 767px) 363px, 26vw'
    }
]

const SLIDE_DURATION = 3000

const stackStyle: CSSProperties = {
    position: 'absolute',
    inset: 0,
    width: '100%',
    height: '100%'
}

export const PhoneSlider: FC = (): ReactNode => {
    const containerRef = useRef<HTMLDivElement | null>(null)
    const slideRefs = useRef<(HTMLDivElement | null)[]>([])
    const [index, setIndex] = useState(0)

    const go = (next: number): void => {
        setIndex((next + slides.length) % slides.length)
    }

    useEffect(() => {
        const timer = window.setInterval(() => {
            setIndex(current => (current + 1) % slides.length)
        }, SLIDE_DURATION)

        return () => window.clearInterval(timer)
    }, [])

    useGSAP(
        () => {
            registerGsap()

            slideRefs.current.forEach((slide, slideIndex) => {
                if (!slide) return
                gsap.to(slide, {
                    autoAlpha: slideIndex === index ? 1 : 0,
                    duration: ms(500),
                    ease: wfEase('ease')
                })
            })
        },
        { dependencies: [index], scope: containerRef }
    )

    return (
        <div ref={containerRef} className='phone-slider-container'>
            <div className='phone-slider w-slider'>
                <div className='phone-slider-mask w-slider-mask' style={{ position: 'relative', width: '100%', height: '100%' }}>
                    {slides.map((slide, slideIndex) => (
                        <div
                            key={slide.src}
                            ref={element => {
                                slideRefs.current[slideIndex] = element
                            }}
                            className='phone-slide w-slide'
                            style={{ ...stackStyle, opacity: slideIndex === 0 ? 1 : 0 }}
                        >
                            <img
                                src={slide.src}
                                srcSet={slide.srcSet}
                                sizes={slide.sizes}
                                loading='lazy'
                                alt='San Diego app screen'
                                className='phone-slide-image'
                            />
                        </div>
                    ))}
                </div>

                <div
                    role='button'
                    tabIndex={0}
                    aria-label='Previous slide'
                    onClick={() => go(index - 1)}
                    onKeyDown={event => event.key === 'Enter' && go(index - 1)}
                    className='phone-slider-arrows w-slider-arrow-left'
                >
                    <div className='w-icon-slider-left' />
                </div>

                <div
                    role='button'
                    tabIndex={0}
                    aria-label='Next slide'
                    onClick={() => go(index + 1)}
                    onKeyDown={event => event.key === 'Enter' && go(index + 1)}
                    className='phone-slider-arrows w-slider-arrow-right'
                >
                    <div className='w-icon-slider-right' />
                </div>

                <div className='phone-slide-nav w-slider-nav w-round'>
                    {slides.map((slide, slideIndex) => (
                        <div
                            key={slide.src}
                            role='button'
                            tabIndex={0}
                            aria-label={`Go to slide ${slideIndex + 1}`}
                            onClick={() => setIndex(slideIndex)}
                            onKeyDown={event => event.key === 'Enter' && setIndex(slideIndex)}
                            className={`w-slider-dot${slideIndex === index ? ' w-active' : ''}`}
                        />
                    ))}
                </div>
            </div>
        </div>
    )
}
